import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {SimmsRadio} from "../../../../components/input/SimmsRadio";
import {reportReportResultRadio} from "./InspectionReport";
import {getCellStatus} from "./InspectionReportCellStatus";
import {
    computeDropdownFixedPosition,
    getDropdownPortalStyle,
} from "../../../../utils/selectbox/dropdownViewportPosition";
import "./inspectionReportMatrix.css";

const RESULT_MENU_ESTIMATED_HEIGHT = 168;

const RESULT_OPTION_STATUS = {
    0: "ok",
    1: "ng",
    2: "unknown",
};

function formatResultLabel(value, type) {
    if (parseInt(type) !== 1) {
        return value ?? "";
    }
    if (value === "0" || value === 0) return "정상";
    if (value === "1" || value === 1) return "비정상";
    if (value === "2" || value === 2) return "미확인";
    return value ?? "";
}

function toSelectValue(value) {
    if (value === undefined || value === null || value === "") return "";
    return String(value);
}

function InspectionReportResultCellSelect({value, onChange, name}) {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({top: 0, left: 0, width: 0});
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const status = getCellStatus(value, 1);
    const label = status === "empty" ? "선택" : formatResultLabel(value, 1);
    const currentValue = toSelectValue(value);

    useLayoutEffect(() => {
        if (!open) return undefined;

        const updatePosition = () => {
            const triggerEl = triggerRef.current;
            if (!triggerEl) return;

            const triggerRect = triggerEl.getBoundingClientRect();
            const pos = computeDropdownFixedPosition({
                triggerEl,
                dropdownEl: menuRef.current,
                dropdownMinWidth: Math.ceil(triggerRect.width),
                estimatedHeight: RESULT_MENU_ESTIMATED_HEIGHT,
            });

            setMenuPos({
                ...pos,
                width: Math.ceil(triggerRect.width),
            });
        };

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        const ro = new ResizeObserver(updatePosition);
        if (menuRef.current) ro.observe(menuRef.current);

        const onWin = () => updatePosition();
        window.addEventListener("resize", onWin);
        window.addEventListener("scroll", onWin, true);
        window.visualViewport?.addEventListener("resize", onWin);
        window.visualViewport?.addEventListener("scroll", onWin);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("scroll", onWin, true);
            window.visualViewport?.removeEventListener("resize", onWin);
            window.visualViewport?.removeEventListener("scroll", onWin);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const handleOutside = (e) => {
            if (
                rootRef.current?.contains(e.target) ||
                menuRef.current?.contains(e.target)
            ) {
                return;
            }
            setOpen(false);
        };

        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [open]);

    const options = [
        {value: "", text: "선택", status: "empty"},
        ...reportReportResultRadio.map((opt) => ({
            value: String(opt.value),
            text: opt.text,
            status: RESULT_OPTION_STATUS[opt.value],
        })),
    ];

    return (
        <div ref={rootRef} className={`ir-result-cell-select${open ? " is-open" : ""}`}>
            <button
                ref={triggerRef}
                type="button"
                className={`ir-result-cell-select__trigger ir-overview-cell ir-overview-cell--${status}`}
                onClick={() => setOpen((prev) => !prev)}
                aria-label={name}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="ir-overview-cell__dot" aria-hidden />
                <span className="ir-overview-cell__text ir-result-cell-select__label">{label}</span>
                <span className="ir-result-cell-select__chevron" aria-hidden>▾</span>
            </button>
            {open && typeof document !== "undefined" && createPortal(
                <ul
                    ref={menuRef}
                    className="ir-result-cell-select__menu ir-result-cell-select__menu--portal"
                    role="listbox"
                    aria-label={name}
                    style={{
                        position: "fixed",
                        zIndex: 1100,
                        width: menuPos.width || undefined,
                        minWidth: menuPos.width || undefined,
                        ...getDropdownPortalStyle(menuPos),
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {options.map((opt) => (
                        <li key={opt.value} role="none">
                            <button
                                type="button"
                                role="option"
                                aria-selected={currentValue === opt.value}
                                className={[
                                    "ir-result-cell-select__option",
                                    "ir-overview-cell",
                                    `ir-overview-cell--${opt.status}`,
                                    currentValue === opt.value ? "is-selected" : "",
                                ].join(" ")}
                                onClick={() => {
                                    onChange(opt.value === "" ? undefined : opt.value);
                                    setOpen(false);
                                }}
                            >
                                <span className="ir-overview-cell__dot" aria-hidden />
                                <span className="ir-overview-cell__text">{opt.text}</span>
                            </button>
                        </li>
                    ))}
                </ul>,
                document.body,
            )}
        </div>
    );
}

function InspectionReportResultBadge({value, type}) {
    const label = formatResultLabel(value, type);
    if (!label) {
        return <span className="ir-item-readonly-value ir-item-readonly-value--empty">—</span>;
    }

    if (parseInt(type) === 1) {
        const status = getCellStatus(value, type);
        return (
            <span className={`ir-overview-cell ir-overview-cell--${status}`}>
                <span className="ir-overview-cell__dot" aria-hidden />
                <span className="ir-overview-cell__text">{label}</span>
            </span>
        );
    }

    return (
        <span className="ir-item-readonly-value ir-item-readonly-value--text">
            {label.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                </React.Fragment>
            ))}
        </span>
    );
}

function InspectionReportFieldControl({
    type,
    value,
    onChange,
    name,
    readOnly = false,
    multiline = false,
    variant = "inline",
}) {
    if (readOnly) {
        return <InspectionReportResultBadge value={value} type={type} />;
    }

    if (parseInt(type) === 1) {
        if (variant === "cell") {
            return (
                <InspectionReportResultCellSelect
                    value={value}
                    onChange={onChange}
                    name={name}
                />
            );
        }

        return (
            <div className={`ir-field-radio ir-field-radio--${variant}`}>
                <SimmsRadio
                    title={name}
                    options={reportReportResultRadio}
                    data={value}
                    onChange={onChange}
                />
            </div>
        );
    }

    if (variant === "cell") {
        return (
            <textarea
                className="ir-field-text ir-field-text--cell"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="내용 입력"
                aria-label={name}
                rows={1}
            />
        );
    }

    const fieldClass = [
        "basic-input",
        "ir-field-text",
        variant === "modal" ? "ir-field-text--modal" : "ir-field-text--inline",
    ].join(" ");

    return (
        <textarea
            className={fieldClass}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="내용 입력"
            aria-label={name}
            rows={1}
        />
    );
}

export {InspectionReportFieldControl, InspectionReportResultBadge, formatResultLabel};
